import { useState, useEffect } from 'react';
import { useLang } from '../context/LanguageContext';
import './PollSection.css';

import { API_URL as API } from '../config/env.js';

export default function PollSection({ inline = false }) {
  const { t, lang } = useLang();
  const [poll, setPoll] = useState(null);
  const [voted, setVoted] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchPoll();
  }, []);

  async function fetchPoll() {
    try {
      const res = await fetch(`${API}/api/poll`);
      const data = await res.json();
      setPoll(data);
      const savedVote = localStorage.getItem(`rk_poll_${data.id}`);
      if (savedVote) setVoted(savedVote);
    } catch {
      setPoll(null);
    }
    setLoading(false);
  }

  async function handleVote(optionId) {
    if (voted || submitting) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/poll/vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pollId: poll.id, optionId }),
      });
      const data = await res.json();
      setPoll(data);
      setVoted(optionId);
      localStorage.setItem(`rk_poll_${poll.id}`, optionId);
    } catch {/* ignore */}
    setSubmitting(false);
  }

  const totalVotes = poll?.options?.reduce((s, o) => s + o.votes, 0) || 0;

  const getPercent = (votes) =>
    totalVotes === 0 ? 0 : Math.round((votes / totalVotes) * 100);

  if (loading) return null;
  if (!poll || !poll.options?.length) return null;

  return (
    <section className={`poll-section ${inline ? 'poll-inline' : ''}`} id="poll" data-sub-free>
      <div className="poll-frame">
        <div className="poll-frame-inner">
          <header className="poll-head">
            <div className="poll-head-main">
              <h2 className="poll-head-title">{t.pollTitle || 'మీ అభిప్రాయం'}</h2>
              <p className="poll-head-hint">{t.pollHeadHint}</p>
            </div>
            <p className="poll-votes-line">
              <span className="poll-votes-num">{totalVotes}</span>
              <span className="poll-votes-label">{t.pollVotes || 'votes'}</span>
            </p>
          </header>

          {lang !== 'te' && (
            <p className="poll-lang-tip">{t.pollLangTip}</p>
          )}

          <p className="poll-question">{poll.question}</p>

          <div className="poll-choices">
            {poll.options.map((opt, i) => {
              const pct = getPercent(opt.votes);
              const isVoted = voted === opt.id;
              const isWinner =
                voted && opt.votes === Math.max(...poll.options.map((o) => o.votes));

              return (
                <button
                  key={opt.id}
                  type="button"
                  className={`poll-choice ${voted ? 'poll-choice-reveal' : ''} ${isVoted ? 'poll-choice-mine' : ''} ${isWinner && voted ? 'poll-choice-top' : ''}`}
                  onClick={() => handleVote(opt.id)}
                  disabled={!!voted || submitting}
                  aria-label={`${i + 1}. ${opt.label}`}
                  aria-pressed={isVoted}
                >
                  <span
                    className="poll-choice-meter"
                    style={{ transform: voted ? `scaleX(${pct / 100})` : 'scaleX(0)' }}
                  />
                  <span className="poll-choice-label">{opt.label}</span>
                  {voted && (
                    <span className="poll-choice-side">
                      {isVoted && <span className="poll-choice-tick">✓</span>}
                      <span className="poll-choice-pct">
                        {pct}
                        <span className="poll-choice-pct-unit">%</span>
                      </span>
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {voted && <p className="poll-foot">{t.pollThanks || 'మీ ఓటు నమోదైంది.'}</p>}
        </div>
      </div>
    </section>
  );
}
